import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PrivacyTipRoundedIcon from '@mui/icons-material/PrivacyTipRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import { Stack } from '@mui/material';

import type { RegistrationMode } from '../../model/settingsTypes';
import { useCustomerSettings, useUpdateCustomerSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import {
  ChipListField,
  FeedbackSnackbar,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
} from '../components';
import { REGISTRATION_MODE_OPTIONS } from '../options';

/** Customer profile, registration, verification and personal-data settings. */
export function CustomerSettingsPage() {
  const { canView, canManage } = useDomainPermissions('customers');
  const query = useCustomerSettings();
  const update = useUpdateCustomerSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success('Customer settings saved.'),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title="Customer settings"
      subtitle="Registration, verification, required profile fields and consent."
      icon={<GroupsRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title="Registration" icon={<GroupsRoundedIcon />}>
              <SelectField
                label="Registration mode"
                value={draft.registrationMode}
                options={REGISTRATION_MODE_OPTIONS}
                onChange={(value) => form.setField('registrationMode', value as RegistrationMode)}
                disabled={disabled}
              />
              <ChipListField
                label="Required profile fields"
                value={draft.requiredProfileFields}
                onChange={(value) => form.setField('requiredProfileFields', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Address book"
                description="Let customers save multiple delivery addresses."
                checked={draft.addressBookEnabled}
                onChange={(value) => form.setField('addressBookEnabled', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title="Verification" icon={<VerifiedUserRoundedIcon />}>
              <SwitchField
                label="Phone verification"
                checked={draft.phoneVerification}
                onChange={(value) => form.setField('phoneVerification', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Email verification"
                checked={draft.emailVerification}
                onChange={(value) => form.setField('emailVerification', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title="Personal data" icon={<PrivacyTipRoundedIcon />}>
              <SwitchField
                label="Consent required"
                description="Require personal-data processing consent."
                checked={draft.personalDataConsentRequired}
                onChange={(value) => form.setField('personalDataConsentRequired', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Anonymization"
                description="Enable customer deletion / anonymization."
                checked={draft.anonymizationEnabled}
                onChange={(value) => form.setField('anonymizationEnabled', value)}
                disabled={disabled}
              />
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
